// The world generator behind the sign-in screen. Pure and deterministic: every function of (u, v)
// or (cu, cv) returns the same answer forever, so the world is infinite, stable, and needs no
// storage. Chunks are 16×16 cells. Nothing here is data about anything real.

export const CH = 16
export const WATER = 0.7 // world height of the lakes: everything below is flooded flat
export const MOUNTAIN = 5.6 // above this the ground is mountain: no trees, no towns, no roads
export const TRACK_SPACING = 120 // world units between rail corridors
export const TRAIN_PERIOD = 300 // world units between trains on a corridor
export const TRAIN_SPEED = 3.5 // world units per second
export const TRAIN_CARS = 4

// ── deterministic noise ───────────────────────────────────────────────────────────────────────
export function hash(x: number, y: number, seed = 0): number {
  let h = (x * 374761393 + y * 668265263 + seed * 1274126177) | 0
  h = (h ^ (h >>> 13)) * 1274126177
  h = (h ^ (h >>> 16)) >>> 0
  return h / 4294967296
}
const smooth = (t: number) => t * t * (3 - 2 * t)
export function valueNoise(x: number, y: number, seed: number): number {
  const x0 = Math.floor(x)
  const y0 = Math.floor(y)
  const tx = smooth(x - x0)
  const ty = smooth(y - y0)
  const a = hash(x0, y0, seed)
  const b = hash(x0 + 1, y0, seed)
  const c = hash(x0, y0 + 1, seed)
  const d = hash(x0 + 1, y0 + 1, seed)
  return (a + (b - a) * tx) * (1 - ty) + (c + (d - c) * tx) * ty
}

/** Relief before flooding: broad hills with folded ridgelines, basins carved by a slow field so
 *  lakes are real depressions with shorelines, and mountain ranges raised by another slow field
 *  with sharp ridged peaks. */
export function rawHeight(u: number, v: number): number {
  const n = valueNoise(u * 0.045, v * 0.045, 1) * 0.6 + valueNoise(u * 0.11, v * 0.11, 2) * 0.28 + valueNoise(u * 0.27, v * 0.27, 3) * 0.12
  const ridged = 1 - Math.abs(n * 2 - 1)
  const basin = Math.max(0, valueNoise(u * 0.028, v * 0.028, 7) - 0.64) * 11
  const range = Math.max(0, valueNoise(u * 0.017, v * 0.017, 8) - 0.66) / 0.34 // 0..1 inside a range
  const peaks = Math.pow(1 - Math.abs(2 * valueNoise(u * 0.09, v * 0.09, 10) - 1), 2)
  return Math.pow(n, 1.3) * 5.4 + ridged * 1.3 - 0.9 - basin + range * (3 + peaks * 6.5)
}
export const groundHeight = (u: number, v: number) => Math.max(rawHeight(u, v), WATER)
export const waterAt = (u: number, v: number) => rawHeight(u, v) < WATER

/** Rail corridor k runs along u at a slowly wandering v. */
export function trackV(u: number, k: number): number {
  return k * TRACK_SPACING + TRACK_SPACING / 2 + (valueNoise(u * 0.012, k * 7.3, 9) - 0.5) * 44
}

// ── chunks ────────────────────────────────────────────────────────────────────────────────────
export interface Building { u: number; v: number; du: number; dv: number; z: number; zh: number }
export interface Town { u: number; v: number; name: string }
export interface Landmark { kind: 'tower' | 'peak'; u: number; v: number; z: number; name: string }
export interface Boat { u: number; v: number; heading: number; phase: number }
export interface Target { u: number; v: number; warm: boolean; name: string }
export interface TrackRun { k: number; pts: { u: number; v: number; z: number; bridge: boolean }[] }
export interface Chunk {
  cu: number
  cv: number
  h: Float32Array // (CH+1)² heights, water flattened
  water: Uint8Array // CH² cells fully under water
  waterCount: number
  town: Town | null
  buildings: Building[]
  streets: [number, number][][]
  roads: [number, number][][] // links to neighbouring towns, in world coords (may leave the chunk)
  trees: { u: number; v: number }[]
  landmark: Landmark | null
  boats: Boat[]
  tracks: TrackRun[]
  targets: Target[] // contacts the pulse reveals as it passes
}

export const callsign = (a: number, b: number, seed: number) => hash(a, b, seed).toString(16).slice(2, 6).toUpperCase()

// ── town sites: decided from pure functions so a neighbour chunk can be asked without generating it
const siteMemo = new Map<string, { u: number; v: number } | null>()
export function townSite(cu: number, cv: number): { u: number; v: number } | null {
  const k = cu + ',' + cv
  const m = siteMemo.get(k)
  if (m !== undefined) return m
  let site: { u: number; v: number } | null = null
  if (hash(cu, cv, 21) < 0.14) {
    const tu = cu * CH + 3 + Math.floor(hash(cu, cv, 22) * (CH - 6))
    const tv = cv * CH + 3 + Math.floor(hash(cu, cv, 23) * (CH - 6))
    let ok = 0
    for (let dv = -2; dv <= 2; dv++)
      for (let du = -2; du <= 2; du++) {
        const u = tu + du, v = tv + dv
        const z = groundHeight(u, v)
        const slope = Math.max(Math.abs(groundHeight(u + 1, v) - z), Math.abs(groundHeight(u, v + 1) - z))
        if (z > WATER && z < MOUNTAIN && slope < 0.45) ok++
      }
    if (ok >= 18) site = { u: tu, v: tv }
  }
  siteMemo.set(k, site)
  return site
}

/** A road from a to b: a greedy walk over cells that prefers gentle dry ground and skirts water
 *  and mountains. Returns cell-centre world coords. */
function pathBetween(a: { u: number; v: number }, b: { u: number; v: number }): [number, number][] {
  const pts: [number, number][] = [[a.u + 0.5, a.v + 0.5]]
  const seen = new Set<string>([a.u + ',' + a.v])
  let u = a.u, v = a.v
  for (let step = 0; step < 120; step++) {
    if (Math.abs(u - b.u) <= 1 && Math.abs(v - b.v) <= 1) { pts.push([b.u + 0.5, b.v + 0.5]); return pts }
    let best: [number, number] | null = null
    let bestCost = Infinity
    const z = groundHeight(u, v)
    for (let dv = -1; dv <= 1; dv++)
      for (let du = -1; du <= 1; du++) {
        if (!du && !dv) continue
        const nu = u + du, nv = v + dv
        if (seen.has(nu + ',' + nv)) continue
        const nz = groundHeight(nu, nv)
        const cost = Math.hypot(nu - b.u, nv - b.v) + Math.abs(nz - z) * 3 + (waterAt(nu, nv) ? 7 : 0) + (nz > MOUNTAIN ? 9 : 0) + (du && dv ? 0.3 : 0)
        if (cost < bestCost) { bestCost = cost; best = [nu, nv] }
      }
    if (!best) break
    ;[u, v] = best
    seen.add(u + ',' + v)
    pts.push([u + 0.5, v + 0.5])
  }
  return pts.length > 1 ? pts : []
}

export function generateChunk(cu: number, cv: number): Chunk {
  const h = new Float32Array((CH + 1) * (CH + 1))
  const water = new Uint8Array(CH * CH)
  for (let lv = 0; lv <= CH; lv++)
    for (let lu = 0; lu <= CH; lu++) h[lv * (CH + 1) + lu] = groundHeight(cu * CH + lu, cv * CH + lv)
  const at = (lu: number, lv: number) => h[lv * (CH + 1) + lu]
  let waterCount = 0
  for (let lv = 0; lv < CH; lv++)
    for (let lu = 0; lu < CH; lu++) {
      const wet = at(lu, lv) <= WATER && at(lu + 1, lv) <= WATER && at(lu, lv + 1) <= WATER && at(lu + 1, lv + 1) <= WATER ? 1 : 0
      water[lv * CH + lu] = wet
      waterCount += wet
    }
  const slope = (lu: number, lv: number) => Math.max(Math.abs(at(lu + 1, lv) - at(lu, lv)), Math.abs(at(lu, lv + 1) - at(lu, lv)))
  const land = (lu: number, lv: number) => lu >= 0 && lv >= 0 && lu < CH && lv < CH && !water[lv * CH + lu] && at(lu, lv) < MOUNTAIN
  const wetAround = (lu: number, lv: number, r: number) => {
    for (let dv = -r; dv <= r; dv++) for (let du = -r; du <= r; du++) { const a = lu + du, b = lv + dv; if (a < 0 || b < 0 || a >= CH || b >= CH || !water[b * CH + a]) return false }
    return true
  }

  const chunk: Chunk = { cu, cv, h, water, waterCount, town: null, buildings: [], streets: [], roads: [], trees: [], landmark: null, boats: [], tracks: [], targets: [] }

  // ── town
  const site = townSite(cu, cv)
  if (site) {
    const tu = site.u - cu * CH, tv = site.v - cv * CH
    chunk.town = { u: site.u, v: site.v, name: 'TWN-' + callsign(cu, cv, 24) }
    const count = 5 + Math.floor(hash(cu, cv, 25) * 9)
    for (let i = 0; i < count; i++) {
      const du = Math.floor(hash(cu, cv, 100 + i) * 7) - 3
      const dv = Math.floor(hash(cu, cv, 200 + i) * 7) - 3
      const lu = tu + du, lv = tv + dv
      const w = hash(cu, cv, 300 + i) < 0.3 ? 2 : 1
      const d = hash(cu, cv, 400 + i) < 0.3 ? 2 : 1
      if (!land(lu, lv) || !land(lu + w - 1, lv + d - 1) || slope(lu, lv) > 0.5) continue
      if (du === 0 || dv === 0) continue
      if (chunk.buildings.some((b) => Math.abs(b.u - (cu * CH + lu)) < 2 && Math.abs(b.v - (cv * CH + lv)) < 2)) continue
      chunk.buildings.push({ u: cu * CH + lu + 0.15, v: cv * CH + lv + 0.15, du: w - 0.3, dv: d - 0.3, z: at(lu, lv), zh: 0.5 + hash(cu, cv, 500 + i) * (hash(cu, cv, 600 + i) < 0.15 ? 3.2 : 1.4) })
    }
    for (const axis of [0, 1] as const) {
      const line: [number, number][] = []
      for (let k = -5; k <= 5; k++) {
        const lu = axis === 0 ? tu + k : tu
        const lv = axis === 0 ? tv : tv + k
        if (!land(lu, lv)) { if (line.length > 1) chunk.streets.push(line.splice(0)); else line.length = 0; continue }
        line.push([cu * CH + lu + 0.5, cv * CH + lv + 0.5])
      }
      if (line.length > 1) chunk.streets.push(line)
    }
    // roads to the towns in the next chunk along +u and along +v (each link owned by one chunk)
    for (const [du, dv] of [[1, 0], [0, 1], [1, 1]] as const) {
      const other = townSite(cu + du, cv + dv)
      if (other) { const p = pathBetween(site, other); if (p.length) chunk.roads.push(p) }
    }
  }

  // ── landmarks: a relay tower on high dry ground, or a named peak where a range tops out
  let peakZ = 0, peakLu = 0, peakLv = 0
  for (let lv = 0; lv < CH; lv++) for (let lu = 0; lu < CH; lu++) if (at(lu, lv) > peakZ) { peakZ = at(lu, lv); peakLu = lu; peakLv = lv }
  if (peakZ > 8.2 && hash(cu, cv, 35) < 0.6) {
    chunk.landmark = { kind: 'peak', u: cu * CH + peakLu, v: cv * CH + peakLv, z: peakZ, name: 'PK-' + callsign(cu, cv, 36) }
  } else if (!chunk.town && hash(cu, cv, 31) < 0.07) {
    const lu = 2 + Math.floor(hash(cu, cv, 32) * (CH - 4))
    const lv = 2 + Math.floor(hash(cu, cv, 33) * (CH - 4))
    if (land(lu, lv) && at(lu, lv) > 2.2) chunk.landmark = { kind: 'tower', u: cu * CH + lu + 0.5, v: cv * CH + lv + 0.5, z: at(lu, lv), name: 'RLY-' + callsign(cu, cv, 34) }
  }

  // ── trees: sparse, on gentle dry mid-ground, denser in the wetter low hills
  for (let lv = 0; lv < CH; lv++)
    for (let lu = 0; lu < CH; lu++) {
      const z = at(lu, lv)
      if (!land(lu, lv) || z < WATER + 0.25 || z > 3.4 || slope(lu, lv) > 0.6) continue
      const density = 0.035 + (z < 1.6 ? 0.05 : 0)
      if (hash(cu * CH + lu, cv * CH + lv, 41) < density && !chunk.buildings.some((b) => Math.abs(b.u - (cu * CH + lu)) < 2 && Math.abs(b.v - (cv * CH + lv)) < 2))
        chunk.trees.push({ u: cu * CH + lu + 0.3 + hash(cu * CH + lu, cv * CH + lv, 42) * 0.4, v: cv * CH + lv + 0.3 + hash(cu * CH + lu, cv * CH + lv, 43) * 0.4 })
    }

  // ── boats: on lakes of thirty cells or more, moored where there is open water all around
  if (waterCount >= 30) {
    const want = waterCount >= 90 ? 2 : 1
    let tries = 0
    while (chunk.boats.length < want && tries < 24) {
      const lu = 2 + Math.floor(hash(cu, cv, 700 + tries) * (CH - 4))
      const lv = 2 + Math.floor(hash(cu, cv, 800 + tries) * (CH - 4))
      tries++
      if (!wetAround(lu, lv, 2)) continue
      if (chunk.boats.some((b) => Math.abs(b.u - (cu * CH + lu)) < 4 && Math.abs(b.v - (cv * CH + lv)) < 4)) continue
      chunk.boats.push({ u: cu * CH + lu + 0.5, v: cv * CH + lv + 0.5, heading: hash(cu, cv, 900 + tries) * Math.PI * 2, phase: hash(cu, cv, 1000 + tries) * Math.PI * 2 })
    }
  }

  // ── rail: the corridors that pass through this chunk, on the ground, on bridges over water
  const k0 = Math.floor((cv * CH - 30) / TRACK_SPACING), k1 = Math.floor((cv * CH + CH + 30) / TRACK_SPACING)
  for (let k = k0; k <= k1; k++) {
    let run: TrackRun | null = null
    for (let u = cu * CH; u <= cu * CH + CH; u++) {
      const v = trackV(u, k)
      if (v < cv * CH || v >= cv * CH + CH) { run = null; continue }
      const g = groundHeight(u, v)
      const bridge = rawHeight(u, v) < WATER
      const z = bridge ? WATER + 0.35 : Math.min(g, MOUNTAIN)
      if (!run) { run = { k, pts: [] }; chunk.tracks.push(run) }
      run.pts.push({ u, v, z, bridge })
    }
  }
  // ── targets: a few contacts per chunk on open ground (or water), revealed as the pulse passes
  const targetCount = hash(cu, cv, 61) < 0.55 ? 1 + Math.floor(hash(cu, cv, 62) * 3) : 0
  for (let i = 0; i < targetCount; i++) {
    const lu = 1 + Math.floor(hash(cu, cv, 1100 + i) * (CH - 2))
    const lv = 1 + Math.floor(hash(cu, cv, 1200 + i) * (CH - 2))
    if (at(lu, lv) > MOUNTAIN) continue
    if (chunk.buildings.some((b) => Math.abs(b.u - (cu * CH + lu)) < 3 && Math.abs(b.v - (cv * CH + lv)) < 3)) continue
    if (chunk.town && Math.abs(chunk.town.u - (cu * CH + lu)) < 5 && Math.abs(chunk.town.v - (cv * CH + lv)) < 5) continue
    chunk.targets.push({ u: cu * CH + lu + 0.5, v: cv * CH + lv + 0.5, warm: hash(cu, cv, 1300 + i) < 0.22, name: 'TGT-' + callsign(cu * 7 + i, cv * 3 - i, 63) })
  }
  return chunk
}
